import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTheme } from '../../context/ThemeContext';
import { CORE_CATEGORIES, MORE_CATEGORIES } from '../../utils/api';
import SearchBar from './SearchBar';

/**
 * Main navigation bar with category links, search, and theme toggle.
 * Uses Bootstrap's collapse with the `show` class for proper animation.
 *
 * @param {{ onSearch: Function }} props
 */
const Navbar = ({ onSearch }) => {
  const { darkMode, toggleTheme } = useTheme();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const collapseRef = useRef(null);

  /** Close mobile menu on route change. */
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  /** Close menu when clicking outside on mobile. */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isOpen && collapseRef.current && !collapseRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  /** Check if a nav link is currently active. */
  const isActive = useCallback(
    (path) => {
      if (path === '/') return location.pathname === '/';
      return location.pathname.startsWith(path);
    },
    [location.pathname]
  );

  /** Build link class with active state. */
  const linkClass = (path) =>
    `nav-link ${isActive(path) ? 'active' : ''}`;

  const navbarClass = darkMode
    ? 'navbar navbar-expand-md navbar-dark qs-navbar'
    : 'navbar navbar-expand-md navbar-light qs-navbar';

  return (
    <nav className={navbarClass}>
      <div className="container">
        <Link className="navbar-brand fw-bold qs-brand" to="/">
          News Quasar
        </Link>

        <button
          className="navbar-toggler"
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon" />
        </button>

        {/* Bootstrap collapse: always has 'collapse', toggles 'show' */}
        <div ref={collapseRef} className={`navbar-collapse collapse ${isOpen ? 'show' : ''}`}>
          <ul className="navbar-nav me-auto mb-2 mb-lg-0">
            {CORE_CATEGORIES.map((cat) => (
              <li className="nav-item" key={cat.id}>
                <Link className={linkClass(`/${cat.id}`)} to={`/${cat.id}`}>
                  {cat.label}
                </Link>
              </li>
            ))}

            {/* More dropdown for secondary categories */}
            <li className="nav-item dropdown">
              <span
                className="nav-link dropdown-toggle"
                role="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                style={{ cursor: 'pointer' }}
              >
                More
              </span>
              <ul className="dropdown-menu">
                {MORE_CATEGORIES.map((cat) => (
                  <li key={cat.id}>
                    <Link
                      className={`dropdown-item ${isActive(`/${cat.id}`) ? 'active' : ''}`}
                      to={`/${cat.id}`}
                      onClick={() => setIsOpen(false)}
                    >
                      {cat.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </li>
          </ul>

          <div className="d-flex align-items-center gap-2">
            <SearchBar onSearch={onSearch} />
            <Link
              to="/bookmarks"
              className={`nav-link ${isActive('/bookmarks') ? 'active' : ''}`}
              title="Bookmarks"
              aria-label="Bookmarks"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill={isActive('/bookmarks') ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
              </svg>
            </Link>
            <button
              className="btn btn-sm qs-theme-toggle"
              onClick={toggleTheme}
              title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {darkMode ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="5" />
                  <line x1="12" y1="1" x2="12" y2="3" />
                  <line x1="12" y1="21" x2="12" y2="23" />
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                  <line x1="1" y1="12" x2="3" y2="12" />
                  <line x1="21" y1="12" x2="23" y2="12" />
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

Navbar.propTypes = {
  onSearch: PropTypes.func.isRequired,
};

Navbar.displayName = 'Navbar';

export default Navbar;
